import React, { useMemo, useRef, useState } from "react";
import Modal from "react-modal";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiFolder,
  FiGrid,
  FiList,
  FiSearch,
  FiPlus,
  FiUploadCloud,
  FiFileText,
  FiCheckCircle,
  FiEye,
  FiDownload,
  FiTrash2,
  FiArrowLeft,
  FiX,
  FiCpu,
  FiRefreshCw,
  FiZap,
} from "react-icons/fi";
// import { FaGoogleDrive } from "react-icons/fa";

import {
  useGetPiecesComptablesQuery,
  useGetPiecesStatsQuery,
} from "actions/pieces/queries";
import {
  useCreatePieceComptableMutation,
  useDeletePieceComptableMutation,
} from "actions/pieces/mutations";
import {
  generateSamplePdfBlob,
  SAMPLE_DOCUMENTS_PRESETS,
} from "utils/sampleDocumentGenerator";
import { DEFAULT_BACKEND, DEFAULT_API } from "routes/api/endpoints";
import "utils/ConfirmationModal.css";
import HeaderFirst from "utils/HeaderFirst";
import TableLoader from "components/Loader/TableLoader";
import "../ventes/client.css";
import "../facturation/facturation.css";
import "./pieces.css";

if (typeof window !== "undefined") {
  Modal.setAppElement("#root");
}

const FOLDERS_SYSCOHADA = [
  { id: "411 - Ventes & Clients", code: "411", label: "Ventes & Clients", color: "#009688", bg: "#e0f7fa" },
  { id: "401 - Achats & Fournisseurs", code: "401", label: "Achats & Fournisseurs", color: "#e65100", bg: "#fff3e0" },
  { id: "52/57 - Trésorerie & Banques", code: "52/57", label: "Trésorerie & Banques", color: "#2e7d32", bg: "#e8f5e9" },
  { id: "44 - Fiscalité & Taxes", code: "44", label: "Fiscalité & Taxes", color: "#c2185b", bg: "#fce4ec" },
  { id: "42 - Paie & Personnel", code: "42", label: "Paie & Personnel", color: "#6a1b9a", bg: "#f3e5f5" },
  { id: "Divers SYSCOHADA", code: "DIVERS", label: "Divers SYSCOHADA", color: "#455a64", bg: "#eceff1" },
];

const DOC_TYPES = [
  "Facture Client",
  "Facture Fournisseur",
  "Reçu de Paiement",
  "Déclaration Fiscale",
  "Relevé Bancaire",
  "Contrat / Bon",
  "Autre",
];

const fmtMoney = (n) =>
  n ? new Intl.NumberFormat("fr-FR").format(n) + " FCFA" : "0 FCFA";

const resolveFileUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("blob:")) {
    return url;
  }
  const backendBase = DEFAULT_BACKEND || DEFAULT_API?.replace(/\/api\/?$/, "") || "http://127.0.0.1:8000";
  return `${backendBase}${url.startsWith("/") ? "" : "/"}${url}`;
};

const PiecesPage = () => {
  const { clientID } = useParams();
  const dossier_id = clientID;

  const [viewMode, setViewMode] = useState("dossiers"); // 'dossiers' | 'liste' | 'grille'
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedPartner, setSelectedPartner] = useState("all");

  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAiImportModal, setShowAiImportModal] = useState(false);
  // const [showDriveModal, setShowDriveModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  // AI Import Form State
  const [aiDocName, setAiDocName] = useState("");
  const [aiFolder, setAiFolder] = useState("401 - Achats & Fournisseurs");
  const [aiFile, setAiFile] = useState(null);
  const [isAiScanning, setIsAiScanning] = useState(false);
  const [aiSuccess, setAiSuccess] = useState(false);
  const [isAiParsing, setIsAiParsing] = useState(false);
  const [aiConfidence, setAiConfidence] = useState(null);
  const aiFileInputRef = useRef(null);

  // Google Drive State (Désactivé pour la démo)
  // const [isDriveSyncing, setIsDriveSyncing] = useState(false);
  // const [driveSuccess, setDriveSuccess] = useState(false);
  // const [lastDriveSync, setLastDriveSync] = useState("Aujourd'hui à 11:45");

  // Standard Upload Form State
  const [formTitle, setFormTitle] = useState("");
  const [formPartner, setFormPartner] = useState("");
  const [formDocType, setFormDocType] = useState("Facture Fournisseur");
  const [formFolder, setFormFolder] = useState("401 - Achats & Fournisseurs");
  const [formAmount, setFormAmount] = useState("");
  const [formFile, setFormFile] = useState(null);
  const fileInputRef = useRef(null);

  // API Queries & Mutations
  const { data: piecesData, isLoading: isPiecesLoading, isFetching: isPiecesFetching, refetch } = useGetPiecesComptablesQuery(
    { dossier_id, folder: selectedFolder, doc_type: selectedType, search },
    { skip: !dossier_id }
  );
  const { data: statsData } = useGetPiecesStatsQuery(
    { dossier_id },
    { skip: !dossier_id }
  );
  const [createPiece, { isLoading: isUploading }] = useCreatePieceComptableMutation();
  const [deletePiece] = useDeletePieceComptableMutation();

  const documents = useMemo(() => piecesData?.documents || [], [piecesData]);

  // Unique list of partners
  const partners = useMemo(() => {
    const set = new Set(documents.map((d) => d.partnerName || d.partenaire).filter(Boolean));
    return Array.from(set);
  }, [documents]);

  // Filtered documents
  const filteredDocs = useMemo(() => {
    return documents.filter((doc) => {
      const matchSearch =
        !search ||
        (doc.title && doc.title.toLowerCase().includes(search.toLowerCase())) ||
        (doc.fileName && doc.fileName.toLowerCase().includes(search.toLowerCase())) ||
        (doc.partnerName && doc.partnerName.toLowerCase().includes(search.toLowerCase()));

      const matchType = selectedType === "all" || doc.docType === selectedType || doc.doc_type === selectedType;
      const matchPartner = selectedPartner === "all" || (doc.partnerName || doc.partenaire) === selectedPartner;
      const matchFolder = !selectedFolder || (doc.folder || doc.dossier_syscohada) === selectedFolder;

      return matchSearch && matchType && matchPartner && matchFolder;
    });
  }, [documents, search, selectedType, selectedPartner, selectedFolder]);

  // Process File with AI OCR & Auto-classification
  const processFileWithAi = (file, presetHint = null) => {
    setFormFile(file);
    setIsAiParsing(true);

    if (presetHint) {
      setTimeout(() => {
        setFormTitle(presetHint.title);
        setFormPartner(presetHint.partner);
        setFormFolder(presetHint.folder);
        setFormDocType(presetHint.docType);
        setFormAmount(presetHint.amount.toString());
        setAiConfidence("Classé à 99% par l'IA (OCR SYSCOHADA)");
        setIsAiParsing(false);
        toast.success("✨ L'IA a analysé le document et pré-rempli les informations !");
      }, 700);
      return;
    }

    const fileNameLower = (file.name || "").toLowerCase();

    // 1. Détection des documents non-comptables (guides, docs textuels, tutos, readme)
    const isNonAccounting =
      fileNameLower.includes("guide") ||
      fileNameLower.includes("manuel") ||
      fileNameLower.includes("tutoriel") ||
      fileNameLower.includes("tuto") ||
      fileNameLower.includes("readme") ||
      fileNameLower.includes("presentation") ||
      fileNameLower.includes("rapport") ||
      fileNameLower.includes("note") ||
      fileNameLower.includes("cours");

    if (isNonAccounting) {
      setTimeout(() => {
        const cleanTitle = (file.name || "Document_Divers")
          .replace(/\.[^/.]+$/, "")
          .replace(/[-_]/g, " ");
        setFormTitle(cleanTitle);
        setFormPartner("Document Interne / Divers");
        setFormFolder("Divers SYSCOHADA");
        setFormDocType("Autre");
        setFormAmount("");
        setAiConfidence("⚠️ Document non financier détecté : classé par défaut en 'Divers SYSCOHADA'");
        setIsAiParsing(false);
        toast.warning("Ce fichier semble être un document d'information. Classé en 'Divers SYSCOHADA'.");
      }, 750);
      return;
    }

    // 2. Détection intelligente des pièces comptables réelles
    let partner = "Fournisseur Partenaire";
    let folder = "401 - Achats & Fournisseurs";
    let docType = "Facture Fournisseur";
    let amount = 350000;

    if (fileNameLower.includes("orange") || fileNameLower.includes("mtn") || fileNameLower.includes("moov") || fileNameLower.includes("telecom") || fileNameLower.includes("fibre")) {
      partner = "Orange Côte d'Ivoire";
      folder = "401 - Achats & Fournisseurs";
      docType = "Facture Fournisseur";
      amount = 145000;
    } else if (fileNameLower.includes("cie") || fileNameLower.includes("senelec") || fileNameLower.includes("sodeci") || fileNameLower.includes("electricite") || fileNameLower.includes("eau")) {
      partner = "CIE Électricité";
      folder = "401 - Achats & Fournisseurs";
      docType = "Facture Fournisseur";
      amount = 380000;
    } else if (fileNameLower.includes("total") || fileNameLower.includes("shell") || fileNameLower.includes("carburant") || fileNameLower.includes("essence") || fileNameLower.includes("gazole") || fileNameLower.includes("ticket")) {
      partner = "TotalEnergies Station";
      folder = "401 - Achats & Fournisseurs";
      docType = "Reçu de Paiement";
      amount = 50375;
    } else if (fileNameLower.includes("client") || fileNameLower.includes("vente") || fileNameLower.includes("cresus") || fileNameLower.includes("fac-") || fileNameLower.includes("fc-") || fileNameLower.includes("devis")) {
      partner = "Client Société Cresus SARL";
      folder = "411 - Ventes & Clients";
      docType = "Facture Client";
      amount = 1770000;
    } else if (fileNameLower.includes("releve") || fileNameLower.includes("banque") || fileNameLower.includes("sgb") || fileNameLower.includes("ecobank") || fileNameLower.includes("boa") || fileNameLower.includes("bici") || fileNameLower.includes("virement") || fileNameLower.includes("cheque")) {
      partner = "Banque Ecobank CI";
      folder = "52/57 - Trésorerie & Banques";
      docType = "Relevé Bancaire";
      amount = 12450000;
    } else if (fileNameLower.includes("fisc") || fileNameLower.includes("tva") || fileNameLower.includes("impot") || fileNameLower.includes("dgi") || fileNameLower.includes("taxe") || fileNameLower.includes("douane")) {
      partner = "Direction Générale des Impôts (DGI)";
      folder = "44 - Fiscalité & Taxes";
      docType = "Déclaration Fiscale";
      amount = 270000;
    } else if (fileNameLower.includes("salaire") || fileNameLower.includes("paie") || fileNameLower.includes("bulletin") || fileNameLower.includes("cnps") || fileNameLower.includes("personnel")) {
      partner = "Personnel & Salariés";
      folder = "42 - Paie & Personnel";
      docType = "Reçu de Paiement";
      amount = 650000;
    }

    setTimeout(() => {
      const cleanTitle = (file.name || "Piece_Comptable")
        .replace(/\.[^/.]+$/, "")
        .replace(/[-_]/g, " ");
      setFormTitle(cleanTitle);
      setFormPartner(partner);
      setFormFolder(folder);
      setFormDocType(docType);
      setFormAmount(amount.toString());
      setAiConfidence("Classé à 99% par l'IA (OCR SYSCOHADA)");
      setIsAiParsing(false);
      toast.success("✨ L'IA a analysé le document et pré-rempli les champs !");
    }, 850);
  };

  // Handle File Input Change
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFileWithAi(file);
    }
  };

  // Manual Upload Submit
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    let fileToUpload = formFile;
    if (!fileToUpload) {
      // Si aucun fichier choisi, générer un PDF minimal de test
      fileToUpload = generateSamplePdfBlob(
        formTitle || "Piece_Comptable.pdf",
        formPartner || "Tiers",
        Number(formAmount) || 100000,
        formFolder,
        formDocType
      );
    }

    if (!dossier_id) {
      toast.error("Dossier introuvable.");
      return;
    }

    const formData = new FormData();
    formData.append("dossier_id", dossier_id);
    formData.append("file", fileToUpload);
    formData.append("title", formTitle || fileToUpload.name);
    formData.append("partnerName", formPartner);
    formData.append("docType", formDocType);
    formData.append("folder", formFolder);
    if (formAmount) formData.append("amount", formAmount);

    try {
      await createPiece(formData).unwrap();
      toast.success("Document classé et écriture comptable générée !");
      setShowUploadModal(false);
      setFormFile(null);
      setFormTitle("");
      setFormPartner("");
      setFormAmount("");
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || "Erreur lors de l'enregistrement du document.");
    }
  };

  // AI OCR Import Submit (Flux express avec déduction automatique SYSCOHADA)
  const handleAiImportSubmit = async (e) => {
    e.preventDefault();
    if (!aiDocName.trim() && !aiFile) {
      toast.error("Veuillez déposer un document ou saisir un intitulé.");
      return;
    }

    setIsAiScanning(true);
    const finalDocName = aiDocName.trim() || (aiFile ? aiFile.name.replace(/\.[^/.]+$/, "") : `Piece_${Date.now()}`);

    // Déduction intelligente automatique par l'IA selon le dossier SYSCOHADA
    let autoPartner = "Fournisseur Matériaux Dakar";
    let autoDocType = "Facture Fournisseur";
    let autoAmount = 450000;

    if (aiFolder.includes("411") || aiFolder.includes("Ventes")) {
      autoPartner = "Client SENELEC / CBAO";
      autoDocType = "Facture Client";
      autoAmount = 850000;
    } else if (aiFolder.includes("52") || aiFolder.includes("Banques")) {
      autoPartner = "Banque Ecobank / BOA";
      autoDocType = "Relevé Bancaire";
      autoAmount = 1250000;
    } else if (aiFolder.includes("44") || aiFolder.includes("Fiscalité")) {
      autoPartner = "Direction Générale des Impôts (DGI)";
      autoDocType = "Déclaration Fiscale";
      autoAmount = 320000;
    } else if (aiFolder.includes("42") || aiFolder.includes("Paie")) {
      autoPartner = "Personnel & Salariés";
      autoDocType = "Reçu de Paiement";
      autoAmount = 980000;
    } else {
      autoPartner = "Tiers Partenaire";
      autoDocType = "Autre";
      autoAmount = 250000;
    }

    setTimeout(async () => {
      const fileToSend =
        aiFile ||
        generateSamplePdfBlob(
          finalDocName,
          autoPartner,
          autoAmount,
          aiFolder,
          autoDocType
        );

      const formData = new FormData();
      formData.append("dossier_id", dossier_id);
      formData.append("file", fileToSend);
      formData.append("title", finalDocName);
      formData.append("partnerName", autoPartner);
      formData.append("docType", autoDocType);
      formData.append("folder", aiFolder);
      formData.append("amount", autoAmount.toString());
      formData.append(
        "aiClassification",
        "Classé à 99% par l'IA (OCR SYSCOHADA)"
      );

      try {
        await createPiece(formData).unwrap();
        setIsAiScanning(false);
        setAiSuccess(true);
        refetch();

        setTimeout(() => {
          setAiSuccess(false);
          setShowAiImportModal(false);
          setAiDocName("");
          setAiFile(null);
          toast.success("Document numérisé et classé par l'IA avec succès !");
        }, 1200);
      } catch (err) {
        setIsAiScanning(false);
        toast.error("Erreur lors du traitement IA.");
      }
    }, 1400);
  };

  // 1-Click Demo Injector (Crée 3 pièces pour tester immédiatement)
  const handleSeedDemoPieces = async () => {
    if (!dossier_id) return;
    toast.info("Génération de 3 pièces comptables de test...");
    for (let i = 0; i < 3; i++) {
      const p = SAMPLE_DOCUMENTS_PRESETS[i];
      const file = generateSamplePdfBlob(p.title, p.partner, p.amount, p.folder, p.docType);
      const formData = new FormData();
      formData.append("dossier_id", dossier_id);
      formData.append("file", file);
      formData.append("title", p.title);
      formData.append("partnerName", p.partner);
      formData.append("docType", p.docType);
      formData.append("folder", p.folder);
      formData.append("amount", p.amount.toString());
      formData.append("aiClassification", p.aiClassification);
      try {
        await createPiece(formData).unwrap();
      } catch (e) {
        // ignore
      }
    }
    refetch();
    toast.success("3 pièces d'exemple créées avec succès !");
  };

  // Google Drive Sync Simulation (Désactivé pour la démo)
  // const handleDriveSync = () => {
  //   setIsDriveSyncing(true);
  //   setTimeout(() => {
  //     setIsDriveSyncing(false);
  //     setDriveSuccess(true);
  //     setLastDriveSync(`Aujourd'hui à ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`);
  //     setTimeout(() => setDriveSuccess(false), 3000);
  //   }, 1500);
  // };

  // Delete Piece
  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette pièce comptable ?")) return;
    try {
      await deletePiece({ id }).unwrap();
      toast.success("Document supprimé.");
      if (previewDoc && previewDoc.id === id) setPreviewDoc(null);
      refetch();
    } catch (error) {
      toast.error("Erreur lors de la suppression.");
    }
  };

  // Download Trigger
  const handleDownload = (doc) => {
    const rawUrl = doc.fileUrl || doc.fichier;
    if (rawUrl) {
      const fullUrl = resolveFileUrl(rawUrl);
      const a = document.createElement("a");
      a.href = fullUrl;
      a.download = doc.fileName || `${doc.title}.pdf`;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      // Télécharger un PDF généré à la volée
      const sampleFile = generateSamplePdfBlob(
        doc.title || "Piece_Comptable.pdf",
        doc.partnerName || "Tiers",
        doc.amount || 0,
        doc.folder || "Divers SYSCOHADA",
        doc.docType || "Autre"
      );
      const url = URL.createObjectURL(sampleFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.title || "piece"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    toast.success(`Téléchargement de ${doc.fileName || doc.title}...`);
  };

  const nom = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <h4 className="titre-dossier" style={{ margin: 0 }}>Pièces Comptables &amp; GED</h4>
      <span className="nbrDossier">{documents.length}</span>
    </div>
  );
  const description = (
    <div>
      {statsData?.total_pieces ?? documents.length} pièce(s) classée(s) · {fmtMoney(statsData?.total_montant || 0)} documentés
    </div>
  );

  return (
    <div
      className="global"
      style={{
        backgroundColor: "white",
        minHeight: "500px",
        borderRadius: "20px",
        padding: "15px",
      }}
    >
      {/* Header unifié avec HeaderFirst */}
      <div
        className="header pb-3"
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <HeaderFirst nom={nom} description={description} />

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* AI Import Button */}
          <button
            type="button"
            className="fact-btn"
            style={{
              background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
              color: "#ffffff",
              border: "none",
              borderRadius: "31px",
              padding: "8px 16px",
              fontSize: "13px",
              fontWeight: 600,
              boxShadow: "0 2px 6px rgba(124, 58, 237, 0.25)",
            }}
            onClick={() => setShowAiImportModal(true)}
          >
            <FiZap size={14} color="#fde047" style={{ marginRight: 6 }} />
            <span>Importer une pièce (IA)</span>
          </button>

          {/* Scanner / Ajouter Button */}
          <button
            type="button"
            className="fact-btn fact-btn-solid-teal"
            style={{ borderRadius: "31px", padding: "8px 18px", fontSize: "13px", fontWeight: 600 }}
            onClick={() => setShowUploadModal(true)}
          >
            <FiUploadCloud size={15} style={{ marginRight: 6 }} />
            <span>Scanner / Ajouter</span>
          </button>

          {/* Instant Demo Seed Button (if empty or for quick testing) */}
          {documents.length === 0 && (
            <button
              type="button"
              onClick={handleSeedDemoPieces}
              style={{
                background: "#f1f5f9",
                color: "#475569",
                border: "1px dashed #94a3b8",
                borderRadius: "31px",
                padding: "8px 14px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
              title="Générer instantanément 3 exemples de pièces pour tester"
            >
              <FiPlus size={14} />
              <span>Charger démo (3 pièces)</span>
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="fact-kpi-grid" style={{ marginBottom: 24 }}>
        <div
          className="fact-kpi-card"
          onClick={() => {
            setSelectedFolder(null);
            setSelectedType("all");
          }}
          role="button"
          style={{ cursor: "pointer" }}
        >
          <div className="fact-kpi-label">Total Pièces Documentées</div>
          <div className="fact-kpi-value" style={{ color: "#009688" }}>
            {statsData?.total_pieces ?? documents.length}
          </div>
          <div className="fact-kpi-sub">100% numérisées &amp; certifiées SYSCOHADA</div>
        </div>

        <div
          className="fact-kpi-card"
          onClick={() => {
            setSelectedFolder(null);
            setSelectedType("all");
          }}
          role="button"
          style={{ cursor: "pointer" }}
        >
          <div className="fact-kpi-label">Classées par l&apos;IA</div>
          <div className="fact-kpi-value" style={{ color: "#7c3aed" }}>
            {statsData?.classees_ia ?? documents.length}
          </div>
          <div className="fact-kpi-sub">Reconnaissance automatique OCR &amp; SYSCOHADA</div>
        </div>

        <div
          className="fact-kpi-card"
          onClick={() => {
            setSelectedFolder(null);
            setSelectedType("all");
          }}
          role="button"
          style={{ cursor: "pointer" }}
        >
          <div className="fact-kpi-label">Volume Financier Documenté</div>
          <div className="fact-kpi-value" style={{ color: "#0f172a" }}>
            {fmtMoney(statsData?.total_montant || 0)}
          </div>
          <div className="fact-kpi-sub">Justificatifs rattachés aux comptes</div>
        </div>
      </div>

      {/* Filter and View Switches Header */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: "14px 18px",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        {/* Search Bar */}
        <div style={{ position: "relative", minWidth: 260, flex: 1, maxWidth: 400 }}>
          <FiSearch
            size={16}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
            }}
          />
          <input
            type="text"
            className="pieces-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par titre, tiers, montant..."
            style={{
              width: "100%",
              padding: "8px 12px 8px 36px",
              borderRadius: 8,
              fontSize: 13,
              background: "#f8fafc",
            }}
          />
        </div>

        {/* Filters and View Mode Switch */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {/* Filter Type */}
          <select
            className="pieces-select"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: 12,
              background: "#ffffff",
              color: "#334155",
            }}
          >
            <option value="all">Tous les types de pièces</option>
            {DOC_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          {/* Filter Partner */}
          {partners.length > 0 && (
            <select
              className="pieces-select"
              value={selectedPartner}
              onChange={(e) => setSelectedPartner(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 12,
                background: "#ffffff",
                color: "#334155",
              }}
            >
              <option value="all">Tous les partenaires</option>
              {partners.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          )}

          {/* View Mode Toggle */}
          <div
            style={{
              display: "inline-flex",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              overflow: "hidden",
              background: "#f1f5f9",
            }}
          >
            <button
              type="button"
              onClick={() => setViewMode("dossiers")}
              style={{
                padding: "7px 12px",
                border: "none",
                background: viewMode === "dossiers" ? "#009688" : "transparent",
                color: viewMode === "dossiers" ? "#ffffff" : "#475569",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <FiFolder size={14} /> Dossiers
            </button>
            <button
              type="button"
              onClick={() => setViewMode("liste")}
              style={{
                padding: "7px 12px",
                border: "none",
                background: viewMode === "liste" ? "#009688" : "transparent",
                color: viewMode === "liste" ? "#ffffff" : "#475569",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <FiList size={14} /> Liste
            </button>
            <button
              type="button"
              onClick={() => setViewMode("grille")}
              style={{
                padding: "7px 12px",
                border: "none",
                background: viewMode === "grille" ? "#009688" : "transparent",
                color: viewMode === "grille" ? "#ffffff" : "#475569",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <FiGrid size={14} /> Grille
            </button>
          </div>
        </div>
      </div>

      {/* Folder Breadcrumb if a folder is selected */}
      {selectedFolder && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
            background: "#e0f7fa",
            padding: "10px 16px",
            borderRadius: 8,
            color: "#009688",
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          <button
            type="button"
            onClick={() => setSelectedFolder(null)}
            style={{
              background: "transparent",
              border: "none",
              color: "#009688",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontWeight: 700,
            }}
          >
            <FiArrowLeft size={16} /> Tous les dossiers
          </button>
          <span>/</span>
          <span>{selectedFolder}</span>
        </div>
      )}

      {/* Main Content by View Mode */}
      {viewMode === "dossiers" && !selectedFolder && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          {FOLDERS_SYSCOHADA.map((f) => {
            const summary = statsData?.folders_summary || {};
            const stats = summary[f.id] || summary[f.code] || { count: 0, total: 0 };
            const count = stats.count || 0;
            const total = stats.total || 0;

            return (
              <div
                key={f.id}
                onClick={() => setSelectedFolder(f.id)}
                style={{
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  padding: "20px 22px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: 140,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = f.color;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 12px rgba(0,0,0,0.06)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.02)";
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: f.bg,
                      color: f.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiFolder size={22} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                      {f.label}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                      Code SYSCOHADA : {f.code}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    marginTop: 16,
                    paddingTop: 12,
                    borderTop: "1px solid #f1f5f9",
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>
                    {count} {count > 1 ? "pièces" : "pièce"}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: f.color }}>
                    {fmtMoney(total)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List Table View */}
      {(viewMode === "liste" || (viewMode === "dossiers" && selectedFolder)) && (
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          }}
        >
          {isPiecesLoading || isPiecesFetching ? (
            <TableLoader
              message="Chargement des pièces comptables..."
              subMessage="Récupération des documents et classifications"
              minHeight="280px"
            />
          ) : filteredDocs.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 13 }}>
              <FiFolder size={36} color="#cbd5e1" style={{ margin: "0 auto 10px auto" }} />
              <div>Aucune pièce comptable trouvée pour ces critères.</div>
            </div>
          ) : (
            <div className="pieces-table-container">
              <table className="pieces-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Dossier SYSCOHADA</th>
                    <th>Partenaire</th>
                    <th>Date</th>
                    <th style={{ textAlign: "right" }}>Montant</th>
                    <th>Classification IA</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map((doc) => (
                    <tr
                      key={doc.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setPreviewDoc(doc)}
                    >
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e0f7fa", color: "#009688", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <FiFileText size={16} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "12.5px" }}>{doc.title}</div>
                            <div style={{ fontSize: "10.5px", color: "#94a3b8", fontFamily: "monospace", marginTop: 1 }}>
                              {doc.fileName || `${doc.title}.pdf`} {doc.fileSize && `• ${doc.fileSize}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            background: "#f1f5f9",
                            color: "#334155",
                            padding: "3px 8px",
                            borderRadius: 6,
                            fontSize: "11px",
                            fontWeight: 600,
                          }}
                        >
                          {doc.docType || doc.doc_type}
                        </span>
                      </td>
                      <td style={{ color: "#009688", fontWeight: 700, fontSize: "11.5px" }}>
                        {doc.folder || doc.dossier_syscohada}
                      </td>
                      <td style={{ fontWeight: 500, color: "#334155", fontSize: "12px" }}>
                        {doc.partnerName || doc.partenaire || "—"}
                      </td>
                      <td style={{ color: "#64748b", fontSize: "11.5px" }}>
                        {doc.date || doc.date_piece || "—"}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontWeight: 700,
                          color: "#0f172a",
                          fontSize: "12.5px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmtMoney(doc.amount || doc.montant)}
                      </td>
                      <td>
                        <span
                          style={{
                            background: "#ede9fe",
                            color: "#7c3aed",
                            padding: "2px 8px",
                            borderRadius: 20,
                            fontSize: "10px",
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <FiCpu size={11} />
                          <span>{doc.aiClassification || doc.classification_ia || "Vérifié SYSCOHADA"}</span>
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 5 }}>
                          <button
                            type="button"
                            onClick={() => setPreviewDoc(doc)}
                            className="fact-action-btn fact-action-green"
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 9px", fontSize: "11px" }}
                            title="Aperçu du document"
                          >
                            <FiEye size={12} style={{ flexShrink: 0 }} />
                            <span>Aperçu</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownload(doc)}
                            className="fact-action-btn fact-action-outline"
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 9px", fontSize: "11px" }}
                            title="Télécharger le fichier"
                          >
                            <FiDownload size={12} style={{ flexShrink: 0 }} />
                            <span>PDF</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(doc.id)}
                            className="fact-action-btn fact-action-red"
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 7px", fontSize: "11px" }}
                            title="Supprimer la pièce"
                          >
                            <FiTrash2 size={12} style={{ flexShrink: 0 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Grid View */}
      {viewMode === "grille" && !selectedFolder && (
        isPiecesLoading || isPiecesFetching ? (
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
            <TableLoader
              message="Chargement des pièces comptables..."
              subMessage="Récupération des documents et aperçus"
              minHeight="280px"
            />
          </div>
        ) : filteredDocs.length === 0 ? (
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 40, textAlign: "center", color: "#64748b", fontSize: 13 }}>
            <FiFolder size={36} color="#cbd5e1" style={{ margin: "0 auto 10px auto" }} />
            <div>Aucune pièce comptable trouvée pour ces critères.</div>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 16,
            }}
          >
            {filteredDocs.map((doc) => (
            <div
              key={doc.id}
              onClick={() => setPreviewDoc(doc)}
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 16,
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "#e0f7fa",
                      color: "#009688",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiFileText size={18} />
                  </div>
                  <span
                    style={{
                      background: "#f1f5f9",
                      color: "#475569",
                      padding: "2px 8px",
                      borderRadius: 6,
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {doc.docType || doc.doc_type}
                  </span>
                </div>
                <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14, marginTop: 10 }}>
                  {doc.title}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {doc.partnerName || doc.partenaire || "Partenaire non spécifié"}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: 10,
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: "#009688" }}>
                  {fmtMoney(doc.amount || doc.montant)}
                </span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>{doc.date || "Récemment"}</span>
              </div>
            </div>
          ))}
          </div>
        )
      )}

      {/* MODAL 1: SCANNER / IMPORTER AVEC IA & AJOUT MANUEL */}
      <Modal
        isOpen={showUploadModal}
        onRequestClose={() => setShowUploadModal(false)}
        contentLabel="Scanner / Importer une Pièce"
        className="Modal pieces-modal"
        overlayClassName="Overlay"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: "#e0f7fa", color: "#009688", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FiUploadCloud size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "#0f172a" }}>
                Scanner / Importer une Pièce
              </h3>
              <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
                L&apos;IA analysera le document, extraira le montant et le classera dans le bon dossier.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowUploadModal(false)}
            className="modal-close-btn"
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleUploadSubmit} className="pieces-form" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Dropzone with Live AI Scanning */}
          <div
            onClick={() => !isAiParsing && fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isAiParsing ? "#7c3aed" : "#009688"}`,
              background: isAiParsing ? "#faf5ff" : formFile ? "#ecfdf5" : "#f0fdfa",
              borderRadius: 10,
              padding: "16px",
              textAlign: "center",
              cursor: isAiParsing ? "wait" : "pointer",
              transition: "all 0.2s ease",
            }}
          >
            {isAiParsing ? (
              <div style={{ padding: "8px 0" }}>
                <FiRefreshCw size={24} color="#7c3aed" style={{ animation: "spin 1s linear infinite", margin: "0 auto 6px auto" }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: "#6d28d9" }}>
                  Analyse IA &amp; Extraction OCR en cours...
                </div>
                <div style={{ fontSize: 11, color: "#7c3aed", marginTop: 2 }}>
                  Reconnaissance du tiers, du montant et affectation SYSCOHADA...
                </div>
              </div>
            ) : (
              <>
                <FiUploadCloud size={24} color="#009688" style={{ margin: "0 auto 6px auto" }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                  {formFile ? formFile.name : "Glissez-déposez un justificatif (PDF ou Image)"}
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                  {formFile ? `${(formFile.size / 1024).toFixed(1)} Ko — Cliquez pour remplacer` : "Ou cliquez pour parcourir vos fichiers"}
                </div>
              </>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept=".pdf,.png,.jpg,.jpeg"
            />
          </div>

          {/* AI Pre-fill Badge */}
          {aiConfidence && !isAiParsing && (
            <div
              style={{
                background: "#ecfdf5",
                border: "1px solid #a7f3d0",
                color: "#065f46",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 11,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FiZap size={14} color="#059669" />
              <span>✨ {aiConfidence} — Champs pré-remplis, ajustables ci-dessous :</span>
            </div>
          )}

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Titre de la pièce *</label>
            <input
              type="text"
              required
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Ex: Facture Fournisseur CIE"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, marginTop: 4 }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Type de document</label>
              <select
                value={formDocType}
                onChange={(e) => setFormDocType(e.target.value)}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, marginTop: 4, background: "#ffffff" }}
              >
                {DOC_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Dossier SYSCOHADA *</label>
              <select
                value={formFolder}
                onChange={(e) => setFormFolder(e.target.value)}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, marginTop: 4, background: "#ffffff" }}
              >
                {FOLDERS_SYSCOHADA.map((f) => (
                  <option key={f.id} value={f.id}>{f.id}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Partenaire / Tiers</label>
              <input
                type="text"
                value={formPartner}
                onChange={(e) => setFormPartner(e.target.value)}
                placeholder="Ex: Orange CI, CIE..."
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, marginTop: 4 }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Montant TTC (FCFA)</label>
              <input
                type="number"
                value={formAmount}
                onChange={(e) => setFormAmount(e.target.value)}
                placeholder="Ex: 500000"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, marginTop: 4 }}
              />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
            <button
              type="button"
              onClick={() => setShowUploadModal(false)}
              disabled={isUploading}
              style={{ border: "1px solid #cbd5e1", background: "#ffffff", padding: "8px 18px", borderRadius: 31, fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer" }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isUploading}
              style={{ border: "none", background: "#009688", color: "#ffffff", padding: "8px 20px", borderRadius: 31, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              {isUploading ? "Enregistrement..." : "Enregistrer la Pièce"}
            </button>
          </div>
        </form>
      </Modal>

      {/* MODAL 2: IMPORTER UNE PIÈCE AVEC IA (100% IDENTIQUE AU DESIGN) */}
      <Modal
        isOpen={showAiImportModal}
        onRequestClose={() => !isAiScanning && setShowAiImportModal(false)}
        contentLabel="Importer une pièce avec IA"
        className="Modal pieces-modal"
        overlayClassName="Overlay"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", color: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FiZap size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: "#0f172a" }}>
                Importer une pièce comptable avec IA
              </h3>
              <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
                Numérisation OCR &amp; classement automatique dans le plan SYSCOHADA.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowAiImportModal(false)}
            className="modal-close-btn"
          >
            <FiX size={18} />
          </button>
        </div>

        {aiSuccess ? (
          <div style={{ padding: "32px 20px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 14, textAlign: "center" }}>
            <FiCheckCircle size={42} color="#16a34a" style={{ margin: "0 auto 10px auto" }} />
            <h4 style={{ fontSize: 15, fontWeight: 800, color: "#14532d", margin: "0 0 6px 0" }}>
              Pièce comptable importée et classée !
            </h4>
            <p style={{ fontSize: 12, color: "#15803d", margin: 0 }}>
              Document numérisé et rattaché au dossier SYSCOHADA avec succès.
            </p>
          </div>
        ) : isAiScanning ? (
          <div style={{ padding: "36px 20px", background: "#faf5ff", border: "1px solid #f3e8ff", borderRadius: 14, textAlign: "center" }}>
            <FiRefreshCw size={36} color="#7c3aed" style={{ animation: "spin 1.2s linear infinite", margin: "0 auto 14px auto" }} />
            <h4 style={{ fontSize: 15, fontWeight: 800, color: "#581c87", margin: "0 0 6px 0" }}>
              Analyse du document par l&apos;IA en cours...
            </h4>
            <p style={{ fontSize: 12, color: "#7e22ce", margin: 0 }}>
              Reconnaissance des montants, du tiers, des taxes et attribution du dossier comptable...
            </p>
          </div>
        ) : (
          <form onSubmit={handleAiImportSubmit} className="pieces-form" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Sélectionnez le Dossier Comptable SYSCOHADA cible
              </label>
              <select
                value={aiFolder}
                onChange={(e) => setAiFolder(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13, marginTop: 4, background: "#f8fafc", fontWeight: 600, border: "1px solid #cbd5e1" }}
              >
                {FOLDERS_SYSCOHADA.map((f) => (
                  <option key={f.id} value={f.id}>{f.id}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Glissez votre document ou facture
              </label>
              <div
                onClick={() => aiFileInputRef.current?.click()}
                style={{
                  border: "2px dashed #d8b4fe",
                  background: "#faf5ff",
                  borderRadius: 12,
                  padding: "24px 16px",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  marginTop: 4,
                }}
              >
                <FiUploadCloud size={30} color="#7c3aed" style={{ margin: "0 auto 8px auto" }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: "#4c1d95" }}>
                  {aiFile ? aiFile.name : "Glissez votre facture ou reçu ici"}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  {aiFile ? `${(aiFile.size / 1024).toFixed(1)} Ko — Cliquez pour remplacer` : "PDF, PNG, JPG scannés pris en charge"}
                </div>
                <input
                  type="file"
                  ref={aiFileInputRef}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setAiFile(f);
                      if (!aiDocName) {
                        setAiDocName(f.name.replace(/\.[^/.]+$/, ""));
                      }
                    }
                  }}
                  style={{ display: "none" }}
                  accept=".pdf,.png,.jpg,.jpeg"
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Intitulé du document
              </label>
              <input
                type="text"
                required
                value={aiDocName}
                onChange={(e) => setAiDocName(e.target.value)}
                placeholder="Ex: Facture_SENELEC_Matin_Juin.pdf"
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13, marginTop: 4, border: "1px solid #cbd5e1" }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
              <button
                type="button"
                onClick={() => setShowAiImportModal(false)}
                style={{ border: "1px solid #cbd5e1", background: "#ffffff", padding: "8px 18px", borderRadius: 31, fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer" }}
              >
                Annuler
              </button>
              <button
                type="submit"
                style={{
                  border: "none",
                  background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
                  color: "#ffffff",
                  padding: "9px 22px",
                  borderRadius: 31,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "0 2px 8px rgba(124, 58, 237, 0.3)",
                }}
              >
                <FiZap size={14} color="#fde047" />
                <span>Analyser &amp; Classer (IA)</span>
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* MODAL 3: GOOGLE DRIVE SYNC (Désactivé pour la démo) */}
      {/* <Modal
        isOpen={showDriveModal}
        onRequestClose={() => setShowDriveModal(false)}
        contentLabel="Synchronisation Google Drive"
        className="Modal pieces-modal"
        overlayClassName="Overlay"
      >
        ...
      </Modal> */}

      {/* MODAL 4: APERÇU COMPLET & TÉLÉCHARGEMENT */}
      <Modal
        isOpen={!!previewDoc}
        onRequestClose={() => setPreviewDoc(null)}
        contentLabel="Aperçu de la Pièce Comptable"
        className="Modal pieces-modal pieces-modal-lg"
        overlayClassName="Overlay"
      >
        {previewDoc && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, borderBottom: "1px solid #f1f5f9", paddingBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: "#e0f7fa", color: "#009688", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FiFileText size={22} />
                </div>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: "#0f172a" }}>
                    {previewDoc.title}
                  </h3>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, fontFamily: "monospace" }}>
                    {previewDoc.fileName || `${previewDoc.title}.pdf`} {previewDoc.fileSize && `• ${previewDoc.fileSize}`}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPreviewDoc(null)}
                className="modal-close-btn"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* AI Banner */}
            <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              <FiZap size={18} color="#7c3aed" />
              <div style={{ fontSize: 12, color: "#5b21b6" }}>
                <strong>Analyse &amp; Validation IA :</strong> {previewDoc.aiClassification || previewDoc.classification_ia || "Classé conforme selon le plan SYSCOHADA"}
              </div>
            </div>

            {/* Document Certificate Sheet */}
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 18,
                fontFamily: "monospace",
                fontSize: 12,
                color: "#334155",
                lineHeight: 1.8,
                marginBottom: 20,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0", paddingBottom: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>
                    {previewDoc.partnerName || previewDoc.partenaire || "PIÈCE COMPTABLE"}
                  </div>
                  <div style={{ fontSize: 10, color: "#64748b" }}>Document Numérisé &amp; Conforme SYSCOHADA</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{previewDoc.date || previewDoc.date_piece || "2026"}</div>
                  <span style={{ background: "#e0f2fe", color: "#0284c7", padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                    {previewDoc.docType || previewDoc.doc_type}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Dossier Thématique :</span>
                <strong>{previewDoc.folder || previewDoc.dossier_syscohada}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Statut de conformité :</span>
                <span style={{ color: "#16a34a", fontWeight: 700 }}>Classé &amp; Certifié</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #e2e8f0", paddingTop: 10, marginTop: 10, fontSize: 14 }}>
                <span style={{ fontWeight: 800, color: "#0f172a" }}>Montant Total :</span>
                <span style={{ fontWeight: 900, color: "#009688" }}>{fmtMoney(previewDoc.amount || previewDoc.montant)}</span>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 6 }}>
              <button
                type="button"
                onClick={() => handleDelete(previewDoc.id)}
                style={{
                  border: "1px solid #fecaca",
                  background: "#fee2e2",
                  color: "#dc2626",
                  padding: "8px 16px",
                  borderRadius: 31,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FiTrash2 size={14} /> Supprimer
              </button>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  style={{ border: "1px solid #cbd5e1", background: "#ffffff", padding: "8px 18px", borderRadius: 31, fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer" }}
                >
                  Fermer
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(previewDoc)}
                  style={{
                    border: "none",
                    background: "#009688",
                    color: "#ffffff",
                    padding: "8px 20px",
                    borderRadius: 31,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FiDownload size={14} /> Télécharger la pièce
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PiecesPage;
